import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FileCheck2, FileText } from "lucide-react";

import { EmptyState } from "../../components/EmptyState";
import { Modal } from "../../components/Modal";
import { useToast } from "../../components/Toast";
import {
  Badge,
  Button,
  Cargando,
  ErrorCarga,
  Input,
  PageHeader,
  PromptDialog,
  TableWrap,
  Textarea,
} from "../../components/ui";
import { formatCLP, mensajeDeError } from "../../lib/api";
import { getAuth, hasPermission } from "../../lib/auth";
import { fechaCorta } from "../../lib/format";
import { formatRut } from "../../lib/rut";
import {
  ESTADOS,
  ORDEN_ESTADOS,
  accionDocumento,
  confirmarFolio,
  listarDocumentos,
  tipoLabel,
  useEstadoDte,
  type AccionDte,
  type DocumentoTributario,
  type EstadoDocumento,
} from "./api";

const POR_PAGINA = 50;

/** Las acciones vuelven a emitir en segundo plano: refrescar un par de veces. */
const REFRESCOS_MS = [1500, 5000];

interface AccionDef {
  accion: AccionDte;
  boton: string;
  titulo: string;
  descripcion: string;
  confirmar: string;
  supervisor?: boolean;
  tono?: "danger" | "default";
}

const ACCIONES: Partial<Record<EstadoDocumento, AccionDef[]>> = {
  Error: [
    {
      accion: "reintentar",
      boton: "Reintentar",
      titulo: "Volver a emitir",
      descripcion:
        "Se toman los datos actuales del socio (RUT, nombre, dirección) y se envía de nuevo. Corrige antes en la ficha del socio lo que el proveedor rechazó.",
      confirmar: "Volver a emitir",
    },
  ],
  Incierto: [
    {
      accion: "conciliar",
      boton: "Volver a buscar",
      titulo: "Buscar en el proveedor",
      descripcion:
        "Se busca en el proveedor un documento con el mismo RUT, fecha, tipo y total. Si aparece uno solo, queda emitido con ese folio.",
      confirmar: "Buscar",
    },
    {
      accion: "reemitir-incierto",
      boton: "Re-emitir",
      titulo: "Re-emitir un documento sin confirmar",
      descripcion:
        "Sólo si revisaste el panel del proveedor y el documento NO salió. Si salió, quedará duplicado en el SII y habrá que anular uno con nota de crédito.",
      confirmar: "Re-emitir",
      supervisor: true,
      tono: "danger",
    },
  ],
  Rechazado_SII: [
    {
      accion: "reemitir",
      boton: "Re-emitir con folio nuevo",
      titulo: "Re-emitir con folio nuevo",
      descripcion:
        "La rechazada queda como registro (Reemplazada) y se emite una nueva con los datos actuales del socio y la fecha de hoy. La deuda del socio no cambia.",
      confirmar: "Re-emitir",
    },
  ],
};

export function DocumentosSiiPage(): JSX.Element {
  const [params, setParams] = useSearchParams();
  const estado = (params.get("estado") ?? "") as EstadoDocumento | "";
  const cierreIdRaw = params.get("cierre_id");
  const cierreId = cierreIdRaw ? Number(cierreIdRaw) : undefined;
  const pagina = Math.max(0, Number(params.get("pagina") ?? 0) || 0);

  const estadoQ = useEstadoDte();
  const docsQ = useQuery({
    queryKey: ["dte", "documentos", estado, cierreId ?? null, pagina],
    queryFn: () =>
      listarDocumentos({
        estado: estado || undefined,
        cierreId,
        limit: POR_PAGINA,
        offset: pagina * POR_PAGINA,
      }),
  });

  const cambiar = (clave: string, valor: string | null): void => {
    const p = new URLSearchParams(params);
    if (valor) p.set(clave, valor);
    else p.delete(clave);
    if (clave !== "pagina") p.delete("pagina");
    setParams(p, { replace: true });
  };

  const qc = useQueryClient();
  const toast = useToast();
  const auth = getAuth();
  const esSupervisor = auth != null && hasPermission(auth, "cierres.cerrar");
  const puedeFacturar = auth != null && hasPermission(auth, "cierres.facturar");
  const [abierta, setAbierta] = useState<{
    doc: DocumentoTributario;
    def: AccionDef;
  } | null>(null);
  const [folioDe, setFolioDe] = useState<DocumentoTributario | null>(null);

  const refrescar = (): void => {
    for (const ms of [0, ...REFRESCOS_MS]) {
      window.setTimeout(
        () => void qc.invalidateQueries({ queryKey: ["dte"] }),
        ms,
      );
    }
  };

  const mut = useMutation({
    mutationFn: ({
      doc,
      def,
      motivo,
    }: {
      doc: DocumentoTributario;
      def: AccionDef;
      motivo: string;
    }) => accionDocumento(doc.id, def.accion, motivo),
    onSuccess: (_res, { def }) => {
      setAbierta(null);
      toast.success(
        def.accion === "conciliar"
          ? "Buscando en el proveedor: el estado se actualiza en unos segundos."
          : "Enviado al proveedor: el estado se actualiza en unos segundos.",
      );
      refrescar();
    },
    onError: (err) =>
      toast.error(mensajeDeError(err, "No se pudo completar la acción")),
  });

  const total = docsQ.data?.total ?? 0;
  const paginas = Math.max(1, Math.ceil(total / POR_PAGINA));
  const conteos = estadoQ.data?.por_estado ?? {};

  return (
    <div className="space-y-6">
      <PageHeader
        title="Documentos SII"
        icon={FileCheck2}
        subtitle="Boletas y notas de crédito electrónicas: cómo van ante el SII y las que hay que resolver."
        volver={
          cierreId != null
            ? { ruta: `/cierres/${cierreId}`, etiqueta: "Volver al cierre" }
            : undefined
        }
      />

      {estadoQ.data && !estadoQ.data.habilitado && (
        <div className="rounded-lg border border-neutral-200 bg-neutral-50 px-4 py-3 text-sm text-neutral-700">
          Este comité todavía no emite documentos tributarios electrónicos. Lo
          activa la oficina cuando el comité está certificado ante el SII.
        </div>
      )}

      <div
        className="flex flex-wrap gap-2"
        role="group"
        aria-label="Filtrar por estado"
      >
        <FiltroEstado
          activo={estado === ""}
          onClick={() => cambiar("estado", null)}
        >
          Todos
        </FiltroEstado>
        {ORDEN_ESTADOS.filter((e) => (conteos[e] ?? 0) > 0 || e === estado).map(
          (e) => (
            <FiltroEstado
              key={e}
              activo={estado === e}
              onClick={() => cambiar("estado", e)}
            >
              {ESTADOS[e].label}
              <span
                className={`ml-1.5 tabular-nums ${estado === e ? "text-white/85" : "text-neutral-500"}`}
              >
                {conteos[e] ?? 0}
              </span>
            </FiltroEstado>
          ),
        )}
        {cierreId != null && (
          <button
            type="button"
            onClick={() => cambiar("cierre_id", null)}
            className="rounded-full border border-brand-300 bg-brand-50 px-3 py-1 text-sm text-brand-800 hover:bg-brand-100"
          >
            Sólo este cierre · quitar filtro
          </button>
        )}
      </div>

      {estado && ESTADOS[estado] && (
        <p className="text-sm text-neutral-600">{ESTADOS[estado].ayuda}</p>
      )}

      {docsQ.isPending && <Cargando />}
      {docsQ.isError && (
        <ErrorCarga
          error={docsQ.error}
          onReintentar={() => void docsQ.refetch()}
        />
      )}
      {docsQ.data && docsQ.data.items.length === 0 && (
        <EmptyState
          icon={FileText}
          title="Sin documentos con ese filtro"
          description="Prueba con otro estado o quita el filtro del cierre."
        />
      )}
      {docsQ.data && docsQ.data.items.length > 0 && (
        <TableWrap label="Documentos tributarios">
          <table className="min-w-full text-sm">
            <thead className="bg-neutral-50 text-xs uppercase tracking-wide text-neutral-500">
              <tr>
                <th className="px-3 py-2 text-left font-semibold">Documento</th>
                <th className="px-3 py-2 text-left font-semibold">Socio</th>
                <th className="px-3 py-2 text-left font-semibold">Período</th>
                <th className="px-3 py-2 text-right font-semibold">Total</th>
                <th className="px-3 py-2 text-left font-semibold">Estado</th>
                <th className="px-3 py-2 text-right font-semibold">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {docsQ.data.items.map((d) => (
                <FilaDocumento
                  key={d.id}
                  doc={d}
                  esSupervisor={esSupervisor}
                  puedeFacturar={puedeFacturar}
                  onAccion={(def) => setAbierta({ doc: d, def })}
                  onConfirmarFolio={() => setFolioDe(d)}
                />
              ))}
            </tbody>
          </table>
        </TableWrap>
      )}

      {paginas > 1 && (
        <nav
          className="flex items-center justify-between text-sm"
          aria-label="Paginación"
        >
          <span className="text-neutral-600">
            {total.toLocaleString("es-CL")} documentos · página {pagina + 1} de{" "}
            {paginas}
          </span>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              disabled={pagina === 0}
              onClick={() => cambiar("pagina", String(pagina - 1))}
            >
              Anterior
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={pagina + 1 >= paginas}
              onClick={() => cambiar("pagina", String(pagina + 1))}
            >
              Siguiente
            </Button>
          </div>
        </nav>
      )}

      <PromptDialog
        open={abierta != null}
        title={abierta?.def.titulo ?? ""}
        description={abierta?.def.descripcion}
        label="Motivo"
        placeholder="Por qué, para la bitácora"
        maxLength={500}
        confirmLabel={abierta?.def.confirmar ?? ""}
        tone={abierta?.def.tono ?? "default"}
        loading={mut.isPending}
        onConfirm={(motivo) => abierta && mut.mutate({ ...abierta, motivo })}
        onCancel={() => setAbierta(null)}
      />
      {folioDe && (
        <ConfirmarFolioModal
          doc={folioDe}
          open
          onClose={() => setFolioDe(null)}
          onListo={refrescar}
        />
      )}
    </div>
  );
}

function FiltroEstado({
  activo,
  onClick,
  children,
}: {
  activo: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={activo}
      className={
        activo
          ? "rounded-full border border-brand-700 bg-brand-700 px-3 py-1 text-sm font-medium text-white"
          : "rounded-full border border-neutral-300 bg-white px-3 py-1 text-sm text-neutral-700 hover:bg-neutral-50"
      }
    >
      {children}
    </button>
  );
}

function FilaDocumento({
  doc,
  esSupervisor,
  puedeFacturar,
  onAccion,
  onConfirmarFolio,
}: {
  doc: DocumentoTributario;
  esSupervisor: boolean;
  puedeFacturar: boolean;
  onAccion: (def: AccionDef) => void;
  onConfirmarFolio: () => void;
}): JSX.Element {
  const acciones = (ACCIONES[doc.estado] ?? []).filter((a) =>
    a.supervisor ? esSupervisor : puedeFacturar,
  );
  const info = ESTADOS[doc.estado];

  return (
    <tr className="align-top hover:bg-neutral-50">
      <td className="px-3 py-2 whitespace-nowrap">
        <div className="font-medium text-neutral-900">
          {tipoLabel(doc.tipo)}
        </div>
        <div className="text-xs text-neutral-600">
          {doc.folio != null
            ? `Folio ${doc.folio}`
            : `N° interno ${doc.numero_interno ?? "—"}`}
          {doc.fecha_emision && ` · ${fechaCorta(doc.fecha_emision)}`}
        </div>
        {doc.folio_referencia != null && (
          <div className="text-xs text-neutral-600">
            Anula el folio {doc.folio_referencia}
          </div>
        )}
      </td>
      <td className="px-3 py-2">
        {doc.socio_id != null ? (
          <Link
            to={`/socios/${doc.socio_id}/cuenta`}
            className="font-medium text-neutral-900 hover:text-brand-700"
          >
            {doc.socio_nombre ?? doc.razon_social_receptor}
          </Link>
        ) : (
          <span className="font-medium text-neutral-900">
            {doc.razon_social_receptor}
          </span>
        )}
        <div className="text-xs text-neutral-600">
          {formatRut(doc.rut_receptor)}
        </div>
      </td>
      <td className="px-3 py-2 whitespace-nowrap text-neutral-700">
        {doc.periodo ?? "—"}
      </td>
      <td className="px-3 py-2 text-right font-mono tabular-nums text-neutral-800">
        {formatCLP(doc.monto_total)}
      </td>
      <td className="px-3 py-2 max-w-xs">
        <Badge tone={info.tono}>{info.label}</Badge>
        {doc.respuesta_sii && doc.estado !== "Emitido" && (
          <p className="mt-1 text-xs text-neutral-600 break-words">
            {doc.respuesta_sii}
          </p>
        )}
      </td>
      <td className="px-3 py-2">
        <div className="flex flex-wrap justify-end gap-2">
          {acciones.map((a) => (
            <Button
              key={a.accion}
              size="sm"
              variant={a.tono === "danger" ? "secondary" : "primary"}
              onClick={() => onAccion(a)}
            >
              {a.boton}
            </Button>
          ))}
          {doc.estado === "Incierto" && esSupervisor && (
            <Button size="sm" variant="secondary" onClick={onConfirmarFolio}>
              Confirmar folio
            </Button>
          )}
        </div>
      </td>
    </tr>
  );
}

function ConfirmarFolioModal({
  doc,
  open,
  onClose,
  onListo,
}: {
  doc: DocumentoTributario;
  open: boolean;
  onClose: () => void;
  onListo: () => void;
}): JSX.Element {
  const toast = useToast();
  const [folio, setFolio] = useState("");
  const [motivo, setMotivo] = useState("");
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: () => confirmarFolio(doc.id, Number(folio), motivo.trim()),
    onSuccess: () => {
      toast.success(`Folio ${folio} confirmado.`);
      setFolio("");
      setMotivo("");
      onClose();
      onListo();
    },
    onError: (err) =>
      setError(mensajeDeError(err, "No se pudo confirmar el folio")),
  });

  const enviar = (e: React.FormEvent): void => {
    e.preventDefault();
    if (!/^\d+$/.test(folio) || Number(folio) <= 0) {
      setError("Escribe el folio tal como aparece en el panel del proveedor.");
      return;
    }
    if (!motivo.trim()) {
      setError("Falta el motivo.");
      return;
    }
    setError(null);
    mut.mutate();
  };

  return (
    <Modal open={open} onClose={onClose} title="Confirmar el folio a mano">
      <form onSubmit={enviar} className="space-y-4">
        <p className="text-sm text-neutral-700">
          Usa esto sólo si viste el documento en el panel del proveedor:{" "}
          {tipoLabel(doc.tipo)} a {doc.razon_social_receptor} (
          {formatRut(doc.rut_receptor)}) por {formatCLP(doc.monto_total)}.
        </p>
        <Input
          label="Folio"
          inputMode="numeric"
          value={folio}
          onChange={(e) => setFolio(e.target.value.trim())}
          required
        />
        <Textarea
          label="Motivo"
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          maxLength={500}
          rows={2}
          required
        />
        {error && (
          <p role="alert" className="text-sm text-rose-700">
            {error}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" disabled={mut.isPending}>
            {mut.isPending ? "Confirmando…" : "Confirmar folio"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
